import { useMemo, useRef } from 'react';
import type { ListFilter } from '@/api/generated/schemas';
import {
  DEFAULT_LIST_PICKER_MODE,
  DEFAULT_PICK_MODAL_WIDTH,
  DEFAULT_SELECTOR_PAGE_SIZE,
} from '../constants';
import { AsyncSelector } from '../field/AsyncSelector';
import type { AsyncSelectorProps } from '../field/AsyncSelector.types';
import { PickFilterProvider } from '../filters/PickFilterProvider';
import { usePickFilterValues } from '../filters/pickFilterHooks';
import { defaultPickFilterValues } from '../filters/pickFilterValues';
import { SelectorPickFilterBar } from '../filters/SelectorPickFilterBar';
import type {
  AsyncSearchParams,
  AsyncSelectorHandle,
  BoundSelectorProps,
  CreateEntityAsyncSelectorOptions,
  EntityAsyncSelectorProps,
  PickFilterValues,
} from '../types';

type BridgedAsyncSelectorProps<TItem> = Omit<
  AsyncSelectorProps<TItem>,
  keyof BoundSelectorProps<TItem>
>;

function bridgedPropsFromOptions<TItem, TFilters extends PickFilterValues>(
  options: CreateEntityAsyncSelectorOptions<TItem, TFilters>,
  readFilters: () => TFilters,
  readExtraFilters: () => ListFilter[]
): BridgedAsyncSelectorProps<TItem> {
  return {
    columns: options.columns,
    getKey: options.getKey,
    renderSelected: options.renderSelected,
    renderSelectedSecondary: options.renderSelectedSecondary,
    isItemSelected: options.isItemSelected,
    search: (params) => options.search(params, readFilters(), readExtraFilters()),
    queryKey: (params) => options.queryKey(params, readFilters(), readExtraFilters()),
    resolveByIds: options.resolveByIds
      ? (ids) => options.resolveByIds!(ids, readFilters(), readExtraFilters())
      : undefined,
    objectCategory: options.objectCategory,
    defaultPageSize: options.defaultPageSize ?? DEFAULT_SELECTOR_PAGE_SIZE,
    listPicker: options.listPicker ?? DEFAULT_LIST_PICKER_MODE,
    pickModalTitle: options.pickModalTitle,
    pickModalWidth: options.pickModalWidth ?? DEFAULT_PICK_MODAL_WIDTH,
    form: options.form,
    renderPickToolbar:
      options.pickFilters && options.pickFilters.length > 0 ? SelectorPickFilterBar : undefined,
  };
}

function EntitySelectorInner<TItem, TFilters extends PickFilterValues>({
  options,
  readFilters,
  filterResetKey,
  extraFilters,
  ...props
}: BoundSelectorProps<TItem> & {
  options: CreateEntityAsyncSelectorOptions<TItem, TFilters>;
  readFilters: () => TFilters;
  filterResetKey?: TFilters;
}) {
  const filtersRef = useRef(readFilters());
  filtersRef.current = readFilters();

  // Fixní call-site filtry (extraFilters) čteme přes ref, aby `bridged` zůstalo memoizované na [options].
  const extraFiltersRef = useRef<ListFilter[]>(extraFilters ?? []);
  extraFiltersRef.current = extraFilters ?? [];

  const bridged = useMemo(
    () =>
      bridgedPropsFromOptions(
        options,
        () => filtersRef.current,
        () => extraFiltersRef.current
      ),
    [options]
  );

  return <AsyncSelector<TItem> {...props} {...bridged} filterResetKey={filterResetKey} />;
}

function EntitySelectorWithFilters<TItem, TFilters extends PickFilterValues>({
  options,
  ...props
}: BoundSelectorProps<TItem> & {
  options: CreateEntityAsyncSelectorOptions<TItem, TFilters>;
}) {
  const filterValues = usePickFilterValues<TFilters>();
  const filtersRef = useRef(filterValues);
  filtersRef.current = filterValues;

  return (
    <EntitySelectorInner
      {...props}
      options={options}
      readFilters={() => filtersRef.current}
      filterResetKey={filterValues}
    />
  );
}

function EntitySelectorWithoutFilters<TItem, TFilters extends PickFilterValues>({
  options,
  defaultFilters,
  ...props
}: BoundSelectorProps<TItem> & {
  options: CreateEntityAsyncSelectorOptions<TItem, TFilters>;
  defaultFilters: TFilters;
}) {
  const filtersRef = useRef(defaultFilters);
  return (
    <EntitySelectorInner {...props} options={options} readFilters={() => filtersRef.current} />
  );
}

export function EntityAsyncSelector<TItem, TFilters extends PickFilterValues = PickFilterValues>({
  options,
  ...props
}: EntityAsyncSelectorProps<TItem, TFilters>) {
  const defaultFilters = defaultPickFilterValues(options.pickFilters) as TFilters;
  const hasPickFilters = (options.pickFilters?.length ?? 0) > 0;

  if (hasPickFilters) {
    return (
      <PickFilterProvider filters={options.pickFilters}>
        <EntitySelectorWithFilters options={options} {...props} />
      </PickFilterProvider>
    );
  }

  return (
    <EntitySelectorWithoutFilters options={options} defaultFilters={defaultFilters} {...props} />
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function createEntityAsyncSelector<
  TItem,
  TFilters extends PickFilterValues = PickFilterValues,
>(
  options: CreateEntityAsyncSelectorOptions<TItem, TFilters>
): AsyncSelectorHandle<TItem, TFilters> {
  const defaultFilters = defaultPickFilterValues(options.pickFilters) as TFilters;

  const queryKey = (params: AsyncSearchParams, filterValues: TFilters = defaultFilters) =>
    options.queryKey(params, filterValues);

  function EntitySelectorComponent(props: BoundSelectorProps<TItem>) {
    return <EntityAsyncSelector options={options} {...props} />;
  }

  return Object.assign(EntitySelectorComponent, {
    queryKey,
    defaultPageSize: options.defaultPageSize ?? DEFAULT_SELECTOR_PAGE_SIZE,
  });
}
